import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from './axios-base-query';
import APIS from '../../utils/apiUrl';
import { SearchResponse } from '../../utils/types';
import { User, UserStatus } from '../../models/user';
import { BaseSearchRequest } from '../../models/shared';

export interface SearchUsersRequest extends BaseSearchRequest<User> {
  roles?: number[];
  statuses?: UserStatus[];
  organizationIds?: string[];
  searchTerm?: string;
}

/**
 * A redux-toolkit query api for users. The toolkit manages refreshes, caching, cancellation, etc
 * via its generated react hooks (see the exports at the bottom of the file). The hooks return useful
 * helper states like loading, error, or data
 *
 * @see https://redux-toolkit.js.org/rtk-query/overview
 */
export const userApi = createApi({
  reducerPath: 'users',
  baseQuery: axiosBaseQuery({ baseUrl: APIS.PORTAL_API }),
  tagTypes: [],
  refetchOnMountOrArgChange: true,
  endpoints: builder => ({
    searchUsers: builder.query<SearchResponse<User>, SearchUsersRequest>({
      query: params => ({
        url: '/api/users/search',
        method: 'get',
        params: buildParams(params),
      }),
    }),
  }),
});

export const {
  /** a hook to search for users, see {@link SearchUsersRequest} for parameters */
  useSearchUsersQuery,
} = userApi;

const buildParams = (request: SearchUsersRequest): URLSearchParams => {
  const params: URLSearchParams = new URLSearchParams();
  params.append('pageNumber', request.pageNumber.toString());
  params.append('pageSize', request.pageSize.toString());
  if (request.searchTerm) {
    params.append('searchTerm', request.searchTerm);
  }
  if (request.organizationIds && request.organizationIds.length > 0) {
    request.organizationIds.forEach(orgId => params.append('organizationIds', orgId));
  }
  if (request.statuses && request.statuses.length > 0) {
    request.statuses.forEach(status => params.append('statuses', status ? status : 'undefined'));
  }
  if (request.roles && request.roles.length > 0) {
    request.roles.forEach(role => params.append('roles', role.toString()));
  }
  if (request.sort) {
    params.append('sortProperty', request.sort.property);
    params.append('sortOrder', request.sort.order);
  }
  return params;
};
